import React, { useCallback, useEffect, useRef, useState } from "react";
import countryCodeData from "../../assets/countryCodes.json"
import "./style.scss";

interface ICountryCodesData { name: string, code: string, dial_code: string };

interface ISearchableCountryCodeDropdown {
  name?: string;
  clx?: string;
  onChange: (option: string, countryCode: string) => void;
  defaultVal: string;
  htmlFor?: string;
  wantDefault?: boolean;
  disabled?: boolean;
  hardCodedValue?: string;
}

const SearchableCountryCodeDropdown: React.FC<ISearchableCountryCodeDropdown> = (props) => {
  const { clx = "", onChange, defaultVal, disabled = false, hardCodedValue } = props;
  
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownContainerRef = useRef<HTMLDivElement>(null);

  const [data, setData] = useState<ICountryCodesData[]>(countryCodeData);
  const [selectedOption, setSelectedOption] = useState<string>(defaultVal);
  const [containerVisibility, setContainerVisibility] = useState<boolean>(false);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number>(-1); //for accessibility

  const handleOptionSelect = (option: ICountryCodesData) => {
    setSelectedOption(option.dial_code);
    setContainerVisibility(false);
    onChange(option.dial_code, option.code);
    setData(countryCodeData.filter(item => item.dial_code === option.dial_code))
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.trim().toLocaleUpperCase();
    setSelectedOption("");
    setContainerVisibility(true);
    setFocusedOptionIndex(-1);
  
    if (!inputValue) {
      setData(countryCodeData);
      return;
    }
  
    const filteredData = countryCodeData.filter(
      (option) =>
        option.dial_code.includes(inputValue) || option.code.includes(inputValue)
    );
    setData(filteredData);
  
    if (filteredData.some((o) => o.dial_code === inputValue)) {
      setSelectedOption(inputValue);
      onChange(inputValue, (filteredData.find((o) => o.dial_code === inputValue)?.code || ''));
    }
  };
  

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!containerVisibility) return;
    else setSelectedOption("");
    switch (e.key) {
      case 'ArrowDown':
        setFocusedOptionIndex((prevIndex) => (prevIndex + 1) % data.length);
        break;
      case 'ArrowUp':
        setFocusedOptionIndex((prevIndex) => prevIndex <= 0 ? -1 : (prevIndex - 1 + data.length) % data.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedOptionIndex >= 0 && focusedOptionIndex < data.length) handleOptionSelect(data[focusedOptionIndex]);
        break;
      default:
        break;
    }
  };

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setContainerVisibility(false);
        setFocusedOptionIndex(-1);
        if (!selectedOption) {
          setSelectedOption(defaultVal);
          setData(countryCodeData);
          onChange(defaultVal, 'IN');
        }
      }
    }, [selectedOption]);

  useEffect(() => {
    if (focusedOptionIndex >= 0 && dropdownContainerRef.current) {
      const focusedElement = dropdownContainerRef.current.querySelector(
        `.country-code-options-list li:nth-child(${focusedOptionIndex + 1})`
      ) as HTMLElement;
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [focusedOptionIndex]);  

  useEffect(() => {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
  }, [handleClickOutside]);

  useEffect(() => {
    if (!!hardCodedValue) {
      setSelectedOption(hardCodedValue);
      setData(countryCodeData.filter(item => item.dial_code === hardCodedValue))
    } else {
      onChange(defaultVal, (countryCodeData.find((o) => o.dial_code === defaultVal)?.code || 'IN'));
      setSelectedOption(defaultVal);
      setData(countryCodeData);
    }
  }, [hardCodedValue, defaultVal])

  return (
      <div className={`searchable-input-dropdown-wrapper ${disabled ? "disabled-styling" : ""} ${clx}`} onKeyDown={handleKeyDown} ref={selectRef}>
        <input
          type="text"
          className={disabled ? "disabled-styling" : ""}
          placeholder={focusedOptionIndex >= 0 ? data[focusedOptionIndex]?.dial_code : defaultVal}
          value={!!hardCodedValue ? hardCodedValue : focusedOptionIndex >= 0 ? data[focusedOptionIndex]?.dial_code : countryCodeData.find((o) => o?.dial_code === selectedOption)?.dial_code}
          onChange={handleInputChange}
          onClick={() => setContainerVisibility(true)}
          disabled={disabled}
        />
        {containerVisibility && (
          <div className="options-container" id="dropdown-scroll-container" ref={dropdownContainerRef}>
            <ul className="country-code-options-list">
              {!!data.length ? (
                data.map((option, index) => (
                  <li key={option?.code} onClick={() => handleOptionSelect(option)} className={index === focusedOptionIndex ? 'focused' : ''}>
                    {`${option?.dial_code} (${option?.code})`}
                  </li>
                ))
              ) : (
                <li>NA</li>
              )}
            </ul>
          </div>
        )}
      </div>
  );
};

export default SearchableCountryCodeDropdown;
